/* ============================================================================
   LISIBILITÉ GÉOMÉTRIQUE d'une scène (complément de src/fidelity.js)
   ----------------------------------------------------------------------------
   La fidélité vérifie que la scène DIT la même chose que le texte ; ce module
   vérifie qu'elle est LISIBLE : pas de pions superposés, pas de flèche qui
   traverse un joueur étranger à l'action, pas de grappe illisible.
   Purement géométrique (aucun rendu nécessaire), volontairement CONSERVATEUR :
   un signalement = un vrai problème visuel probable, pas une nuance.
   Renvoie [{code, msg}] — codes :
     - "superposition"  : deux pions à moins de 5 unités (le rond d'un joueur
                          fait ~2.5 unités de rayon : à <5 ils se touchent) ;
     - "sur_marqueur"   : un pion posé SUR un plot/cône/cible (<3.5) — le
                          ballon porté et les bulles de consigne sont exclus ;
     - "fleche_traverse": une flèche ordonnée passe À TRAVERS un joueur qui
                          n'est ni son départ ni son arrivée (distance au
                          TRACÉ DESSINÉ < 2.5, hors abords des extrémités) —
                          le tracé est la courbe de contournement quand il y en
                          a une (src/detour.js), la polyligne auteur quand la
                          scène en porte une (le relais suit son couloir), le
                          segment droit sinon ;
     - "grappe"         : ≥ 4 entités dans un rayon de 8 (grappe illisible) —
                          les positions d'ARRIVÉE des courses y comptent ;
     - "echange_plat"   : (filet) ≥ 3 frappes retombant sur la même bande
                          horizontale — aucune profondeur lisible.
   Les positions ÉVOLUENT : les courses/conduites déplacent leur acteur — les
   contrôles utilisent la position COURANTE au moment de chaque action.
   ========================================================================== */

import { arrowDetour, arrowDistance } from "./detour.js";

const BALLS = new Set(["ballon", "ball", "balle", "disque", "frisbee", "volant"]);
const SOFT_MARKERS = new Set(["consigne", "cote", "essai", "contact", "zone"]);

export function legibilityIssues(scene, sport) {
  const issues = [];
  const push = (code, msg) => issues.push({ code, msg });
  const players = (Array.isArray(scene?.players) ? scene.players : [])
    .filter((p) => Number.isFinite(+p.x) && Number.isFinite(+p.y));
  const markers = Array.isArray(scene?.markers) ? scene.markers : [];
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];

  // 1) SUPERPOSITION de pions (positions de DÉPART — c'est l'état affiché).
  //    Deux ADVERSAIRES proches, c'est souvent un MARQUAGE/duel voulu : on ne
  //    signale le duel qu'au vrai chevauchement (<3) ; même équipe : <5.
  for (let i = 0; i < players.length; i++)
    for (let j = i + 1; j < players.length; j++) {
      const a = players[i], b = players[j];
      // Un MUR (coup franc / jet franc) est une ligne volontairement compacte.
      if (/^mur/i.test(a.role || "") && /^mur/i.test(b.role || "")) continue;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const sameSide = (a.team || "") === (b.team || "");
      if (d < (sameSide ? 5 : 3)) push("superposition", `${a.id} et ${b.id} superposés (distance ${d.toFixed(1)})`);
    }

  // 2) PION SUR UN MARQUEUR dur (plot, cône, cible…) — le ballon au pied du
  //    porteur et les éléments « doux » (consigne, cote, zone) sont normaux.
  for (const m of markers) {
    const type = String(m.type || "").toLowerCase();
    if (BALLS.has(type) || SOFT_MARKERS.has(type) || !Number.isFinite(+m.x)) continue;
    for (const p of players) {
      const d = Math.hypot(p.x - m.x, p.y - m.y);
      if (d < 3.5) push("sur_marqueur", `${p.id} posé sur un ${type} (distance ${d.toFixed(1)})`);
    }
  }

  // 3) FLÈCHE À TRAVERS UN JOUEUR étranger à l'action — en suivant l'évolution
  //    des positions (une course déplace son acteur pour les actions suivantes).
  //    Le contrôle porte sur le TRACÉ DESSINÉ : le rendu courbe désormais les
  //    flèches qui passeraient sur un pion (`arrowDetour`, src/detour.js), et
  //    c'est la MÊME fonction qui est appelée ici — le banc ne peut pas
  //    mesurer une géométrie que le rendu ne dessine pas. Quand aucune courbe
  //    acceptable n'existe (hors terrain, ou un autre pion sur le chemin),
  //    elle renvoie null et le défaut est signalé comme avant.
  const pos = {};
  for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
  const ordered = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  for (const a of ordered) {
    const authored = Array.isArray(a.path) && a.path.length >= 2 ? a.path : null;
    const A = authored ? authored[0]
      : (a.fromx != null ? { x: +a.fromx, y: +a.fromy } : pos[a.from]);
    const B = authored ? authored[authored.length - 1]
      : (a.to != null ? pos[a.to] : (a.tox != null ? { x: +a.tox, y: +a.toy } : null));
    // POLYLIGNE AUTEUR : exemptée. Une trajectoire posée par un gabarit qui
    //   possède la géométrie n'est pas un tracé à corriger — c'est le
    //   DISPOSITIF. Cas mesuré : le relais d'athlétisme. La course d'un
    //   relayeur suit SON couloir sur toute sa tranche, et longe forcément les
    //   coureurs suivants de son équipe, postés plus loin dans ce même couloir.
    //   Les signaler serait un contresens (c'est un relais), et le rendu validé
    //   par l'enseignant les dessine ainsi. Mesuré sur le lot v41 : 41
    //   signalements, TOUS de cette nature, 0 vrai défaut. Même raison que la
    //   neutralisation de `sanitizeScene` sur les scènes `stadePlace` — le
    //   gabarit a déjà placé au millimètre.
    if (A && B && !authored) {
      const path = arrowDetour(a, A, B, players, pos);
      const actor = players.find((p) => p.id === a.from);
      for (const p of players) {
        if (p.id === a.from || p.id === a.to) continue;
        // TIR / COURSE / CONDUITE à travers un ADVERSAIRE : c'est souvent le
        // GESTE DÉCRIT (tir devant le gardien, fixation, « dans le dos ») —
        // seul un PARTENAIRE traversé est un vrai défaut. Une PASSE/un CENTRE,
        // eux, sont signalés dans tous les cas (à travers un adversaire =
        // interceptable, à travers un partenaire = illisible).
        if (["tir", "course", "dribble"].includes(a.type)
          && actor && (p.team || "") !== (actor.team || "")) continue;
        const cur = pos[p.id] || p;
        const { d, t } = arrowDistance(cur.x, cur.y, A, B, path);
        // Les abords des extrémités sont exclus : une flèche PART ou ARRIVE
        // naturellement près de quelqu'un (marquage, duel, remise).
        if (d < 2.5 && t > 0.12 && t < 0.88)
          push("fleche_traverse", `l'action ${a.order} (${a.type}${a.from ? " de " + a.from : ""}) traverse ${p.id}`);
      }
    }
    // Suivi de position : course/conduite déplacent l'acteur.
    if ((a.type === "course" || a.type === "dribble") && a.from && B && a.to == null) pos[a.from] = { x: B.x, y: B.y };
  }

  // (3bis retiré — les TRAJECTOIRES DE BALLE superposées sont désormais
  //  DÉCALÉES automatiquement au rendu : deconflictBallSegments, render.js.
  //  Le défaut n'existe plus à l'affichage, le signaler déclencherait des
  //  réparations inutiles en prod.)

  // 3ter) JOUEUR SUR LE FILET (retour prod 👎 volley) : aux sports de filet, un
  //    joueur de champ posé SUR la ligne du filet (x≈50) est illisible — on ne
  //    sait plus dans quel camp il joue. Les contreurs se placent À CÔTÉ.
  if (["badminton", "volleyball", "tennis", "tennis_de_table"].includes(sport)) {
    for (const p of players) {
      if ((p.team || "") !== "A" && (p.team || "") !== "B") continue;
      if (Math.abs(+p.x - 50) < 2.5)
        push("sur_filet", `${p.id} est posé sur le filet (x=${(+p.x).toFixed(1)}) — le placer nettement dans son camp`);
    }
  }

  // 3quater) ÉCHANGE « À PLAT » (sports de filet) — salve proactive nº1 : un
  //    rallye dont TOUTES les frappes retombent sur la même bande horizontale
  //    est illisible ; « long » et « court » y sont indiscernables, et le
  //    schéma ne montre plus le déplacement qu'il est censé enseigner (B02 :
  //    quatre frappes, 10 unités d'amplitude ; B08 : trois frappes, 0). Le
  //    défaut est le MÊME que celui du 👎 prod « pseudo échange pas du tout
  //    dans la zone avant ».
  //    CONSERVATEUR : il faut au moins TROIS frappes de balle et une amplitude
  //    quasi nulle — un aller-retour à deux frappes n'a rien à prouver.
  if (["badminton", "volleyball", "tennis", "tennis_de_table"].includes(sport)) {
    const hits = actions.filter((a) => ["renvoi", "tir"].includes(a.type) && Number.isFinite(+a.toy));
    if (hits.length >= 3) {
      const yy = hits.map((a) => +a.toy);
      const amp = Math.max(...yy) - Math.min(...yy);
      if (amp < 12)
        push("echange_plat", `les ${hits.length} frappes retombent sur la même bande (amplitude ${Math.round(amp)} en y) — varier la PROFONDEUR des frappes (fond / mi-court / filet) pour que le déplacement se voie`);
    }
  }

  // 4) GRAPPE illisible : ≥ 4 entités « dures » dans un rayon de 8.
  //    Les positions d'ARRIVÉE des courses comptent aussi (backlog salve
  //    lisibilité R2/R8 : des grappes bien visibles au coin passaient inaperçues
  //    parce que seules les positions de DÉPART étaient examinées).
  const arrivals = [];
  for (const a of actions) {
    if (!["course", "dribble"].includes(a.type) || !a.from || a.to != null) continue;
    if (!Number.isFinite(+a.tox) || !Number.isFinite(+a.toy)) continue;
    arrivals.push({ x: +a.tox, y: +a.toy, id: `${a.from}↦` });
  }
  const hard = [
    ...players.map((p) => ({ x: +p.x, y: +p.y, id: p.id })),
    ...arrivals,
    ...markers.filter((m) => !BALLS.has(String(m.type || "").toLowerCase()) && !SOFT_MARKERS.has(String(m.type || "").toLowerCase()) && Number.isFinite(+m.x))
      .map((m) => ({ x: +m.x, y: +m.y, id: m.type })),
  ];
  for (const c of hard) {
    const around = hard.filter((o) => Math.hypot(o.x - c.x, o.y - c.y) <= 8);
    if (around.length >= 4) {
      push("grappe", `${around.length} entités groupées autour de (${Math.round(c.x)},${Math.round(c.y)})`);
      break;   // un seul signalement par scène (sinon chaque membre de la grappe répète)
    }
  }

  // Dédoublonnage (paires signalées une fois).
  const seen = new Set();
  return issues.filter((i) => { const k = i.code + "|" + i.msg; if (seen.has(k)) return false; seen.add(k); return true; });
}
